import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  Eye,
  Send,
  Sparkles,
  Calendar,
  MapPin,
  Tag,
  ArrowLeft,
  Loader2,
  Bot
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminBlogsListPage() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [generating, setGenerating] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generateParams, setGenerateParams] = useState({
    template: 'top-events-city',
    city: '',
    sector: '',
    eventId: '',
    month: ''
  });

  useEffect(() => {
    fetchBlogs();
  }, [filter]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(`${BACKEND_URL}/api/admin/blogs${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlogs(response.data.blogs || []);
    } catch (error) {
      toast.error('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/blogs/generate`,
        generateParams,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Blog draft generated!');
      setShowGenerateDialog(false);
      
      // Navigate to edit the new blog
      navigate(`/admin/blogs/${response.data.blog.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate blog');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (blogId) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${BACKEND_URL}/api/admin/blogs/${blogId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Blog deleted');
      fetchBlogs();
    } catch (error) {
      toast.error('Failed to delete blog');
    }
  };

  const handlePublish = async (blogId, currentStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      const endpoint = currentStatus === 'published' ? 'unpublish' : 'publish';
      await axios.post(
        `${BACKEND_URL}/api/admin/blogs/${blogId}/${endpoint}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(currentStatus === 'published' ? 'Blog unpublished' : 'Blog published!');
      fetchBlogs();
    } catch (error) {
      toast.error('Failed to update blog status');
    }
  };

  const filteredBlogs = blogs.filter(blog =>
    blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    blog.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'published':
        return <Badge className="bg-green-100 text-green-800">Published</Badge>;
      case 'draft':
        return <Badge variant="outline" className="text-yellow-700 border-yellow-300">Draft</Badge>;
      case 'scheduled':
        return <Badge className="bg-blue-100 text-blue-800">Scheduled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <Link to="/admin/events" className="text-sm text-gray-500 hover:text-gray-700 flex items-center mb-2">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to Admin
              </Link>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <FileText className="w-8 h-8 mr-3 text-orange-600" />
                Blog Posts
              </h1>
              <p className="text-gray-500 mt-1">Manage and auto-generate blog content</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowGenerateDialog(true)}
                className="flex items-center"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Auto-Generate
              </Button>
              <Link to="/admin/blogs/new">
                <Button className="bg-orange-600 hover:bg-orange-700">
                  <Plus className="w-4 h-4 mr-2" />
                  New Post
                </Button>
              </Link>
            </div>
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex gap-4">
                <Input
                  placeholder="Search blogs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="max-w-xs"
                />
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Posts</SelectItem>
                    <SelectItem value="draft">Drafts</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Blog List */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
            </div>
          ) : filteredBlogs.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700">No blog posts yet</h3>
                <p className="text-gray-500 mb-4">Create your first post or auto-generate content</p>
                <Button onClick={() => setShowGenerateDialog(true)}>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate First Post
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredBlogs.map((blog) => (
                <Card key={blog.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {getStatusBadge(blog.status)}
                          {blog.generatedBy === 'auto' && (
                            <Badge variant="outline" className="text-purple-600 border-purple-300">
                              <Bot className="w-3 h-3 mr-1" />
                              AI Generated
                            </Badge>
                          )}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {blog.title}
                        </h3>
                        <p className="text-sm text-gray-500 mb-2 line-clamp-2">
                          {blog.excerpt}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-gray-400">
                          <span className="flex items-center">
                            <Calendar className="w-3 h-3 mr-1" />
                            {new Date(blog.createdAt).toLocaleDateString()}
                          </span>
                          {blog.city && (
                            <span className="flex items-center">
                              <MapPin className="w-3 h-3 mr-1" />
                              {blog.city}
                            </span>
                          )}
                          {blog.sectorTags?.length > 0 && (
                            <span className="flex items-center">
                              <Tag className="w-3 h-3 mr-1" />
                              {blog.sectorTags.join(', ')}
                            </span>
                          )}
                          <span className="flex items-center">
                            <Eye className="w-3 h-3 mr-1" />
                            {blog.viewCount || 0} views
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {blog.status === 'published' && (
                          <Link to={`/blog/${blog.slug}`} target="_blank">
                            <Button variant="ghost" size="sm">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                        )}
                        <Link to={`/admin/blogs/${blog.id}`}>
                          <Button variant="ghost" size="sm">
                            <Edit className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePublish(blog.id, blog.status)}
                        >
                          <Send className={`w-4 h-4 ${blog.status === 'published' ? 'text-green-600' : ''}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(blog.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Auto-Generate Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
              Auto-Generate Blog Post
            </DialogTitle>
            <DialogDescription>
              Choose a template and parameters to generate blog content automatically.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Type
              </label>
              <Select
                value={generateParams.template}
                onValueChange={(value) => setGenerateParams({ ...generateParams, template: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="top-events-city">Top Events in [City]</SelectItem>
                  <SelectItem value="top-events-sector">Top Events in [Sector]</SelectItem>
                  <SelectItem value="why-attend">Why You Should Attend [Event]</SelectItem>
                  <SelectItem value="accommodation">Group Accommodation for [City] Events</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(generateParams.template === 'top-events-city' || generateParams.template === 'accommodation') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City (slug format, e.g., "london")
                </label>
                <Input
                  value={generateParams.city}
                  onChange={(e) => setGenerateParams({ ...generateParams, city: e.target.value })}
                  placeholder="london"
                />
              </div>
            )}

            {generateParams.template === 'top-events-sector' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sector (e.g., "technology", "healthcare")
                </label>
                <Input
                  value={generateParams.sector}
                  onChange={(e) => setGenerateParams({ ...generateParams, sector: e.target.value })}
                  placeholder="technology"
                />
              </div>
            )}

            {generateParams.template === 'why-attend' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event ID
                </label>
                <Input
                  value={generateParams.eventId}
                  onChange={(e) => setGenerateParams({ ...generateParams, eventId: e.target.value })}
                  placeholder="Enter event ID"
                />
              </div>
            )}

            {generateParams.template === 'accommodation' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Month (optional, e.g., "03" for March)
                </label>
                <Input
                  value={generateParams.month}
                  onChange={(e) => setGenerateParams({ ...generateParams, month: e.target.value })}
                  placeholder="03"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Generate Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
